import { useEffect, useRef } from "react";
import { useScopedPathname } from "@-/com.npmjs.expo-router";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useVoiceStudyLifecycle } from "@-/dev.improve.haniverse.lib.voice-study";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { useDrillPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill.navigator";

/**
 * Exiting through the voice controls or a spoken stop command also leaves Hands-Free: the study
 * environment returns to Standard Study and the deck is re-opened on the same card with
 * `mode: "manual"`. Mounted by the Learn layout so it runs whether or not the deck's header (and
 * its Environment menu) is on screen.
 */
export function VoiceStudyModeExitSync() {
  // Learn's own pathname, so other tabs' navigation never re-renders this; while another tab is
  // in front there is no card on screen to re-open.
  const pathname = useScopedPathname("/learn");
  const isFocused = useIsFocused();
  const navigator = useDrillPageNavigator();
  const lifecycle = useVoiceStudyLifecycle();
  const [studyEnvironment, setStudyEnvironment] = useDeviceSetting("studyEnvironment");
  const [activeDeckCard] = useContextSelectors(LearnLayoutContext, (c) => c.activeDeckCard);
  const reviewCard =
    isFocused && pathname.endsWith("/learn/deck") && activeDeckCard?.kind === "review"
      ? activeDeckCard
      : null;
  const hadVoiceSession = useRef(false);
  // Starting, running, or showing a failure all count: only returning to idle is an exit.
  const hasVoiceSession = lifecycle.kind !== "idle";
  useEffect(() => {
    if (!hasVoiceSession && hadVoiceSession.current) {
      if (studyEnvironment === "hands-free") {
        setStudyEnvironment("standard");
      }
      if (reviewCard?.mode === "voice") {
        navigator.replace.mutate({
          contextSentence: reviewCard.contextSentence,
          drillType: reviewCard.drillType,
          grapheme: reviewCard.grapheme,
          studyMode: "manual",
        });
      }
    }
    hadVoiceSession.current = hasVoiceSession;
  }, [hasVoiceSession, reviewCard, navigator, setStudyEnvironment, studyEnvironment]);
  return null;
}
